import React, { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import type { EligibleContactSummary, MeioEnvioProposta, PropostaAnexo } from '../api';
import {
  useContatosElegiveis,
  useConverterEmContrato,
  useEnviarProposta,
  useRegistrarAceite,
  useRegistrarRejeicao,
} from '../hooks';
import { MEIO_ENVIO_LABEL } from '../utils/format';

/**
 * Modais de transição de estado da proposta (US-COM-006, tasks 16.1,
 * 16.3, 16.4, 16.5). Um arquivo só pra facilitar o import no
 * `PropostaDetalhePage` — mesma disciplina do `BaseComercialActionModals`.
 *
 * Cada modal encapsula um formulário mínimo, valida no client-side
 * antes de disparar a mutation, e propaga erros do backend inline. Os
 * hooks TanStack cuidam da invalidação da árvore de queries.
 */

interface BaseModalProps {
  open: boolean;
  onClose: () => void;
}

function Modal({
  open,
  onClose,
  title,
  children,
}: BaseModalProps & { title: string; children: React.ReactNode }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string | string[] } } }).response;
    const msg = resp?.data?.message;
    if (Array.isArray(msg)) return msg.join('; ');
    if (typeof msg === 'string') return msg;
  }
  return e instanceof Error ? e.message : 'Erro desconhecido.';
}

// -------- 16.1 EnvioPropostaModal ------------------------------------

interface EnvioProps extends BaseModalProps {
  proposalId: string;
  versionId: string;
}

/**
 * Envio ao cliente com upload obrigatório do PDF. O SHA-256 é calculado
 * no backend — para o usuário, o hash aparece na resposta como
 * evidência do que foi enviado (RN-COM-018).
 */
export function EnvioPropostaModal({ open, onClose, proposalId, versionId }: EnvioProps) {
  const enviar = useEnviarProposta(proposalId, versionId);
  const { data: elegiveis } = useContatosElegiveis(proposalId);
  const fileRef = useRef<HTMLInputElement>(null);

  const [selecionados, setSelecionados] = useState<Record<string, boolean>>({});
  const [meio, setMeio] = useState<MeioEnvioProposta>('email');
  const [notes, setNotes] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [hashGerado, setHashGerado] = useState<string | null>(null);

  const meioOptions = (Object.keys(MEIO_ENVIO_LABEL) as MeioEnvioProposta[]).map((m) => ({
    value: m,
    label: MEIO_ENVIO_LABEL[m],
  }));

  async function handleSubmit() {
    setErro(null);
    setHashGerado(null);
    const file = fileRef.current?.files?.[0];
    if (!file) return setErro('Selecione o PDF a enviar ao cliente.');
    const recipients = Object.keys(selecionados)
      .filter((id) => selecionados[id])
      .map((contactId) => ({ contactId }));
    if (recipients.length === 0) return setErro('Selecione ao menos um destinatário.');
    try {
      const result = await enviar.mutateAsync({
        file,
        payload: { recipients, medium: meio, notes: notes.trim() || undefined },
      });
      setHashGerado(result.documentHash);
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Enviar proposta ao cliente">
      {hashGerado ? (
        <div className="space-y-3">
          <Alert variant="success">Envio confirmado.</Alert>
          <div className="rounded bg-gray-50 p-3">
            <span className="text-xs uppercase text-gray-500">SHA-256 do documento</span>
            <p className="mt-1 break-all font-mono text-xs text-gray-900">{hashGerado}</p>
          </div>
          <Button onClick={onClose}>Fechar</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {erro && <Alert variant="error">{erro}</Alert>}
          <div>
            <label className="block text-sm font-medium text-gray-700">Destinatários</label>
            <div className="mt-1 max-h-48 space-y-1 overflow-y-auto rounded border border-gray-200 p-2">
              {(elegiveis ?? []).length === 0 && (
                <span className="text-xs text-gray-500">
                  Nenhum contato ativo — cadastre um contato antes de enviar.
                </span>
              )}
              {(elegiveis ?? []).map((c: EligibleContactSummary) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={!!selecionados[c.id]}
                    onChange={(e) => setSelecionados((s) => ({ ...s, [c.id]: e.target.checked }))}
                  />
                  <span>
                    {c.nome} {c.isPrincipal && <em className="text-xs text-blue-700">(principal)</em>}
                    {c.email && <span className="ml-1 text-xs text-gray-500">— {c.email}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <AutocompleteSelect
            label="Meio de envio *"
            options={meioOptions}
            value={meio}
            onChange={(v) => setMeio(v as MeioEnvioProposta)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700">PDF da proposta *</label>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="mt-1 block w-full text-sm text-gray-600"
            />
            <p className="mt-1 text-xs text-gray-500">
              O SHA-256 do PDF será registrado como evidência do envio.
            </p>
          </div>
          <Textarea
            label="Observações (opcional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={enviar.isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={enviar.isPending}>
              {enviar.isPending ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// -------- 16.3 AceitePropostaModal -----------------------------------

interface AceiteProps extends BaseModalProps {
  proposalId: string;
  versionId: string;
  clientContacts: EligibleContactSummary[];
  attachments: PropostaAnexo[];
}

export function AceitePropostaModal({
  open,
  onClose,
  proposalId,
  versionId,
  clientContacts,
  attachments,
}: AceiteProps) {
  const registrar = useRegistrarAceite(proposalId, versionId);

  const [contactId, setContactId] = useState('');
  const [attachmentId, setAttachmentId] = useState('');
  const [acceptedAt, setAcceptedAt] = useState(new Date().toISOString().slice(0, 10));
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const contactOptions = clientContacts.map((c) => ({
    value: c.id,
    label: c.nome,
    description: c.email ?? undefined,
  }));
  const attachmentOptions = attachments.map((a) => ({
    value: a.id,
    label: a.filename,
  }));

  async function handleSubmit() {
    setErro(null);
    if (!contactId) return setErro('Selecione o contato que aceitou pela empresa.');
    if (!attachmentId)
      return setErro('Anexo comprobatório é obrigatório (RN-COM-021).');
    try {
      await registrar.mutateAsync({
        acceptedByContactId: contactId,
        evidenceAttachmentId: attachmentId,
        acceptedAt,
        observacoes: observacoes.trim() || undefined,
      });
      onClose();
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Registrar aceite formal">
      <div className="space-y-4">
        {erro && <Alert variant="error">{erro}</Alert>}
        <AutocompleteSelect
          label="Contato que aceitou *"
          options={contactOptions}
          value={contactId}
          onChange={setContactId}
        />
        <AutocompleteSelect
          label="Documento comprobatório *"
          options={attachmentOptions}
          value={attachmentId}
          onChange={setAttachmentId}
        />
        <TextInput
          label="Data do aceite *"
          type="date"
          value={acceptedAt}
          onChange={(e) => setAcceptedAt(e.target.value)}
        />
        <Textarea
          label="Observações (opcional)"
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          rows={3}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={registrar.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={registrar.isPending}>
            {registrar.isPending ? 'Registrando…' : 'Registrar aceite'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// -------- 16.4 RejeicaoPropostaModal ---------------------------------

interface RejeicaoProps extends BaseModalProps {
  proposalId: string;
  versionId: string;
}

export function RejeicaoPropostaModal({ open, onClose, proposalId, versionId }: RejeicaoProps) {
  const registrar = useRegistrarRejeicao(proposalId, versionId);

  const [motivo, setMotivo] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [concorrente, setConcorrente] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  async function handleSubmit() {
    setErro(null);
    if (!motivo.trim()) return setErro('Motivo da rejeição é obrigatório.');
    try {
      await registrar.mutateAsync({
        motivo: motivo.trim(),
        justificativa: justificativa.trim() || undefined,
        concorrenteVencedor: concorrente.trim() || undefined,
      });
      onClose();
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Registrar rejeição do cliente">
      <div className="space-y-4">
        {erro && <Alert variant="error">{erro}</Alert>}
        <TextInput
          label="Motivo *"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={2000}
        />
        <Textarea
          label="Justificativa (opcional)"
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          rows={3}
          maxLength={4000}
        />
        <TextInput
          label="Concorrente vencedor (opcional)"
          value={concorrente}
          onChange={(e) => setConcorrente(e.target.value)}
          maxLength={255}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={registrar.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={registrar.isPending}>
            {registrar.isPending ? 'Registrando…' : 'Registrar rejeição'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// -------- 16.5 ConversaoEmContratoModal ------------------------------

interface ConversaoProps extends BaseModalProps {
  proposalId: string;
  versionId: string;
  valorTotal: string;
}

export function ConversaoEmContratoModal({
  open,
  onClose,
  proposalId,
  versionId,
  valorTotal,
}: ConversaoProps) {
  const converter = useConverterEmContrato(proposalId, versionId);

  const [contractType, setContractType] = useState('servicos');
  const [effectiveStart, setEffectiveStart] = useState(new Date().toISOString().slice(0, 10));
  const [effectiveEnd, setEffectiveEnd] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const previewPayload = useMemo(
    () => ({
      contractType,
      effectiveStartDate: effectiveStart,
      effectiveEndDate: effectiveEnd || undefined,
      proposalVersionId: versionId,
      totalValue: valorTotal,
    }),
    [contractType, effectiveStart, effectiveEnd, versionId, valorTotal],
  );

  async function handleSubmit() {
    setErro(null);
    if (!contractType.trim()) return setErro('Tipo de contrato é obrigatório.');
    if (!effectiveStart) return setErro('Data de início da vigência é obrigatória.');
    if (effectiveEnd && effectiveEnd < effectiveStart) {
      return setErro('Data de fim deve ser posterior ao início.');
    }
    try {
      await converter.mutateAsync({
        contractType: contractType.trim(),
        effectiveStartDate: effectiveStart,
        effectiveEndDate: effectiveEnd || undefined,
      });
      onClose();
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Converter em contrato">
      <div className="space-y-4">
        {erro && <Alert variant="error">{erro}</Alert>}
        <TextInput
          label="Tipo de contrato *"
          value={contractType}
          onChange={(e) => setContractType(e.target.value)}
          maxLength={64}
        />
        <TextInput
          label="Data de início da vigência *"
          type="date"
          value={effectiveStart}
          onChange={(e) => setEffectiveStart(e.target.value)}
        />
        <TextInput
          label="Data de fim (opcional)"
          type="date"
          value={effectiveEnd}
          onChange={(e) => setEffectiveEnd(e.target.value)}
        />
        <div className="rounded bg-gray-50 p-3 text-xs">
          <div className="mb-1 font-medium text-gray-700">Payload do evento (preview):</div>
          <pre className="whitespace-pre-wrap break-all text-gray-600">
            {JSON.stringify(previewPayload, null, 2)}
          </pre>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={converter.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={converter.isPending}>
            {converter.isPending ? 'Convertendo…' : 'Converter em contrato'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { Modal };
